/**
 * SubscriptionPage - Manage subscription and billing with stunning modern UI
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { clearCheckoutContext } from '@/utils/checkoutPersistence';
import {
  CurrentPlanCard,
  UsageCard,
  InvoiceList,
  PricingCard,
  BillingPeriodToggle,
} from '@/components/subscription';
import { useSubscriptionStore } from '@/stores';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

function SubscriptionPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    currentSubscription,
    availablePlans,
    usage,
    invoices,
    isLoading,
    fetchCurrentSubscription,
    fetchPlans,
    fetchInvoices,
    createCheckout,
    upgradeSubscription,
    cancelSubscription,
    reactivateSubscription,
    openCustomerPortal,
  } = useSubscriptionStore();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [upgradeBillingPeriod, setUpgradeBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');

  useEffect(() => {
    // Load all subscription data
    const loadData = (force = false) => {
      Promise.all([
        fetchCurrentSubscription(force),
        fetchPlans(force),
        fetchInvoices(),
      ]).catch((err) => {
        console.error('Failed to load subscription data:', err);
        toast.error(t('settings.subscription.failedToLoad'));
      });
    };

    // Initial load
    loadData();

    // Auto-refresh when tab becomes visible (catches plan updates by admin)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Force refresh to bypass 30s cache and get latest plan limits
        loadData(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchCurrentSubscription, fetchPlans, fetchInvoices]);

  // Handle success/cancel from Stripe checkout
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      toast.success(t('settings.subscription.subscriptionUpdated'));
      // Clear any pending checkout context
      clearCheckoutContext();
      // Reload subscription data
      fetchCurrentSubscription();
      // Clear the query params
      searchParams.delete('success');
      setSearchParams(searchParams);
    }

    if (canceled === 'true') {
      toast.info(t('settings.subscription.checkoutCanceled'));
      searchParams.delete('canceled');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, fetchCurrentSubscription]);

  const handleSelectPlan = async (planId: string, billingPeriod: 'monthly' | 'yearly') => {
    try {
      // Validate planId exists in availablePlans
      const targetPlan = availablePlans.find((plan) => plan.id === planId);
      if (!targetPlan) {
        toast.error(t('settings.subscription.invalidPlan'));
        return;
      }

      // Prevent selecting the same plan (duplicate purchase)
      if (currentSubscription && currentSubscription.plan.id === planId) {
        toast.info(t('settings.subscription.alreadySubscribed'));
        return;
      }

      // Check if target plan is FREE
      const isFreeplan = targetPlan.priceMonthly === 0 && targetPlan.priceYearly === 0;

      if (isFreeplan) {
        // Downgrading to free - automatically open cancel dialog
        // Check if user has ANY paid plan (not just checking for stripeSubscriptionId)
        if (currentSubscription && currentSubscription.plan.id !== planId) {
          setShowCancelDialog(true);
        } else {
          toast.info(t('settings.subscription.alreadyFree'));
        }
        return;
      }

      // Check if user has a PAID Stripe subscription (not just a free tier subscription record)
      const hasStripeSubscription = currentSubscription?.stripeCustomerId && currentSubscription?.stripeSubscriptionId;

      if (hasStripeSubscription) {
        // Existing PAID subscription: upgrade/downgrade immediately via backend
        await upgradeSubscription(planId, billingPeriod);
        toast.success(t('settings.subscription.subscriptionUpdated'));
      } else {
        // Free tier OR no subscription: create checkout session and redirect to Stripe for payment
        const checkoutUrl = await createCheckout(
          planId,
          billingPeriod,
          `${window.location.origin}/settings/subscription?success=true`,
          `${window.location.origin}/settings/subscription?canceled=true`
        );
        window.location.href = checkoutUrl;
      }
    } catch (error: any) {
      toast.error(error.message || t('settings.subscription.failedToChangePlan'));
    }
  };

  const handleCancelConfirm = async () => {
    try {
      await cancelSubscription(false); // Cancel at period end
      setShowCancelDialog(false);
      toast.success(t('settings.subscription.canceledAtPeriodEnd'));
    } catch (error: any) {
      toast.error(error.message || t('settings.subscription.failedToCancel'));
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateSubscription();
      toast.success(t('settings.subscription.reactivatedSuccessfully'));
    } catch (error: any) {
      toast.error(error.message || t('settings.subscription.failedToReactivate'));
    }
  };

  const handleManagePayment = async () => {
    try {
      await openCustomerPortal();
    } catch (error: any) {
      toast.error(error.message || t('settings.subscription.failedToOpenPortal'));
    }
  };

  if (isLoading && !currentSubscription && !usage) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center space-y-6">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-primary-500 rounded-full blur-xl opacity-50 animate-pulse" />
            <Loader2 className="relative w-16 h-16 animate-spin text-blue-600 dark:text-blue-400" />
          </div>
          <div className="space-y-2">
            <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('settings.subscription.loadingSubscription')}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('settings.subscription.fetchingDetails')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Filter plan options (exclude current plan, show all others including downgrades)
  const planOptions = availablePlans.filter((plan) => {
    if (currentSubscription) {
      // Exclude only the current plan
      return plan.id !== currentSubscription.plan.id;
    }
    // For users without subscription, show all paid plans
    return plan.priceMonthly > 0;
  });

  return (
    <div className="p-6 space-y-8">
      {/* Current Plan Section */}
        <section className="space-y-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('settings.subscription.currentPlan')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('settings.subscription.currentPlanDesc')}</p>
          </div>
          <CurrentPlanCard
            subscription={currentSubscription}
            onManagePayment={handleManagePayment}
            onCancelSubscription={() => setShowCancelDialog(true)}
            onReactivateSubscription={handleReactivate}
          />
        </section>

        {/* Usage Metrics Section */}
        {usage && (
          <section className="space-y-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('settings.subscription.resourceUsage')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('settings.subscription.resourceUsageDesc')}</p>
            </div>
            <UsageCard usage={usage} />
          </section>
        )}

        {/* Plan Selection Section */}
        {planOptions.length > 0 && (
          <section className="space-y-6">
            <div className="space-y-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {t('settings.subscription.changeYourPlan')}
                  </h2>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">
                    {t('settings.subscription.changeYourPlanDesc')}
                  </p>
                </div>

                <div className="flex-shrink-0">
                  <BillingPeriodToggle
                    period={upgradeBillingPeriod}
                    onChange={setUpgradeBillingPeriod}
                    savingsPercent={17}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {planOptions.map((plan) => (
                  <PricingCard
                    key={plan.id}
                    plan={plan}
                    billingPeriod={upgradeBillingPeriod}
                    isPopular={plan.name.toLowerCase() === 'pro'}
                    onSelectPlan={handleSelectPlan}
                    loading={isLoading}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Billing History Section */}
        <section className="space-y-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('settings.subscription.billingHistory')}
              </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('settings.subscription.billingHistoryDesc')}</p>
          </div>
          <InvoiceList invoices={invoices} loading={isLoading} />
        </section>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="max-w-md border-2 shadow-2xl">
          <AlertDialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/30">
                <Sparkles className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <AlertDialogTitle className="text-2xl font-black">{t('settings.subscription.cancelSubscription')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base space-y-3 leading-relaxed">
              <p className="text-gray-700 dark:text-gray-300">
                {t('settings.subscription.cancelSubscriptionDesc')}
              </p>
              <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <p className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  {t('settings.subscription.keepPremiumFeatures')}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 font-semibold">
              {t('settings.subscription.keepSubscription')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold shadow-lg"
            >
              {t('settings.subscription.cancelSubscriptionBtn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SubscriptionPage;
